import * as React from "react";
import { toast } from "sonner";
import { Hand, Repeat2, CalendarPlus, ArrowRightLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/** Staff loops panel (rendered inside Rotas > My Shifts):
 *  - Open shifts you can claim
 *  - Pending swaps involving you (accept/decline/cancel)
 *  - Availability windows submitter
 */
export default function StaffLoopsPanel({ companyId, canClaim, onChanged, testidPrefix = "staff-loops" }) {
  const [openShifts, setOpenShifts] = React.useState([]);
  const [swaps, setSwaps] = React.useState([]);
  const [availability, setAvailability] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [availDraft, setAvailDraft] = React.useState({ date: "", kind: "unavailable", starts_at: "09:00", ends_at: "17:00", note: "" });

  const load = React.useCallback(async () => {
    try {
      const [oS, sw, av] = await Promise.all([
        api.get(`/companies/${companyId}/rotas/open-shifts`),
        api.get(`/companies/${companyId}/rotas/swaps`, { params: { status: "pending", mine: true } }),
        api.get(`/companies/${companyId}/rotas/availability`),
      ]);
      setOpenShifts(oS.data.open_shifts || []);
      setSwaps(sw.data.swaps || []);
      setAvailability(av.data.availability || []);
    } catch (e) {
      // Silent — panel is optional
    }
  }, [companyId]);
  React.useEffect(() => { load(); }, [load]);

  async function claim(sid) {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/rotas/shifts/${sid}/claim`);
      toast.success("Shift claimed");
      onChanged?.();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  async function decideSwap(swap, decision) {
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/rotas/swaps/${swap.id}/decide`, { decision, note: "" });
      toast.success(`Swap ${decision}`);
      onChanged?.();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  async function submitAvailability() {
    if (!availDraft.date || busy) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/rotas/availability`, availDraft);
      setAvailDraft((s) => ({ ...s, note: "" }));
      toast.success("Availability submitted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  async function deleteAvailability(id) {
    try {
      await api.delete(`/companies/${companyId}/rotas/availability/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-panel`}>
      {/* Open shifts */}
      <div className="card-panel">
        <div className="card-panel-head">
          <div className="flex items-center gap-1.5">
            <Hand size={13} className="text-brand-violet" />
            <div className="text-[12.5px] font-semibold text-brand-ink">Open shifts</div>
            <span className="mono-num text-[10.5px] text-brand-muted">({openShifts.length})</span>
          </div>
        </div>
        <div className="card-panel-body space-y-1">
          {openShifts.length === 0 && (
            <div className="text-[12px] text-brand-muted py-3">No open shifts on your sites.</div>
          )}
          {openShifts.map((s) => (
            <div key={s.id}
                 className="flex items-center gap-2 py-1.5 border-b border-brand-rule/60 last:border-b-0"
                 data-testid={`${testidPrefix}-open-shift-${s.id}`}>
              <span className="mono-num text-[12px] text-brand-ink w-32">
                {new Date(s.start).toISOString().slice(5, 16).replace("T", " ")}
              </span>
              <span className="chip" style={{ background: (s.site_color || "#6E3CFA") + "22", color: s.site_color || "#6E3CFA" }}>
                {s.site_name}
              </span>
              <span className="flex-1 text-[12px] text-brand-muted truncate">{s.position || "—"}</span>
              {canClaim ? (
                <button className="btn-violet !h-7" onClick={() => claim(s.id)} disabled={busy}
                        data-testid={`${testidPrefix}-claim-${s.id}`}>
                  Claim
                </button>
              ) : (
                <span className="text-[10.5px] text-brand-muted">no rotas.claim</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Swaps */}
      <div className="card-panel">
        <div className="card-panel-head">
          <div className="flex items-center gap-1.5">
            <ArrowRightLeft size={13} className="text-brand-violet" />
            <div className="text-[12.5px] font-semibold text-brand-ink">Pending swaps</div>
            <span className="mono-num text-[10.5px] text-brand-muted">({swaps.length})</span>
          </div>
        </div>
        <div className="card-panel-body space-y-1">
          {swaps.length === 0 && (
            <div className="text-[12px] text-brand-muted py-3">No pending swaps.</div>
          )}
          {swaps.map((sw) => (
            <SwapRow key={sw.id} sw={sw} companyId={companyId}
                     onAccept={() => decideSwap(sw, "accepted")}
                     onDecline={() => decideSwap(sw, "declined")}
                     onCancel={() => decideSwap(sw, "cancelled")}
                     busy={busy} testidPrefix={testidPrefix} />
          ))}
        </div>
      </div>

      {/* Availability */}
      <div className="card-panel">
        <div className="card-panel-head">
          <div className="flex items-center gap-1.5">
            <CalendarPlus size={13} className="text-brand-violet" />
            <div className="text-[12.5px] font-semibold text-brand-ink">Availability</div>
            <span className="mono-num text-[10.5px] text-brand-muted">({availability.length})</span>
          </div>
        </div>
        <div className="card-panel-body space-y-2">
          <div className="space-y-1">
            {availability.map((a) => (
              <div key={a.id}
                   className="flex items-center gap-2 py-1.5 border-b border-brand-rule/60 last:border-b-0"
                   data-testid={`${testidPrefix}-avail-${a.id}`}>
                <span className="mono-num text-[12px] text-brand-ink w-24">{a.date}</span>
                <span className={`chip ${a.kind === 'preferred' ? 'chip-emerald' : 'chip-coral'}`}>{a.kind}</span>
                <span className="mono-num text-[11.5px] text-brand-muted w-24">
                  {a.starts_at || "—"} → {a.ends_at || "—"}
                </span>
                <span className="flex-1 text-[12px] text-brand-ink truncate">{a.note || "—"}</span>
                <button className="btn-ghost !h-6 !px-1.5 !text-brand-muted"
                        onClick={() => deleteAvailability(a.id)}
                        data-testid={`${testidPrefix}-avail-rm-${a.id}`}>
                  ×
                </button>
              </div>
            ))}
            {availability.length === 0 && (
              <div className="text-[12px] text-brand-muted py-3">No availability submitted.</div>
            )}
          </div>
          <div className="pt-2 border-t border-brand-rule grid grid-cols-[130px_130px_90px_90px_1fr_auto] gap-2">
            <input type="date" className="field-input !h-8 mono-num"
                   value={availDraft.date}
                   onChange={(e) => setAvailDraft((s) => ({ ...s, date: e.target.value }))}
                   data-testid={`${testidPrefix}-avail-date`} />
            <select className="field-input !h-8"
                    value={availDraft.kind}
                    onChange={(e) => setAvailDraft((s) => ({ ...s, kind: e.target.value }))}
                    data-testid={`${testidPrefix}-avail-kind`}>
              <option value="unavailable">Unavailable</option>
              <option value="preferred">Preferred</option>
            </select>
            <input type="time" className="field-input !h-8 mono-num"
                   value={availDraft.starts_at}
                   onChange={(e) => setAvailDraft((s) => ({ ...s, starts_at: e.target.value }))}
                   data-testid={`${testidPrefix}-avail-start`} />
            <input type="time" className="field-input !h-8 mono-num"
                   value={availDraft.ends_at}
                   onChange={(e) => setAvailDraft((s) => ({ ...s, ends_at: e.target.value }))}
                   data-testid={`${testidPrefix}-avail-end`} />
            <input className="field-input !h-8" placeholder="Note (optional)"
                   value={availDraft.note}
                   onChange={(e) => setAvailDraft((s) => ({ ...s, note: e.target.value }))}
                   data-testid={`${testidPrefix}-avail-note`} />
            <button className="btn-outline !h-8"
                    onClick={submitAvailability}
                    disabled={busy || !availDraft.date}
                    data-testid={`${testidPrefix}-avail-submit`}>
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SwapRow({ sw, companyId, onAccept, onDecline, onCancel, busy, testidPrefix }) {
  const [me, setMe] = React.useState(null);
  React.useEffect(() => {
    try {
      // Cheap: read /auth/me once. Cached by browser.
      api.get("/auth/me").then(({ data }) => setMe(data?.user?.id || null)).catch(() => {});
    } catch (e) { /* noop */ }
  }, []);
  const isRecipient = me === sw.to_user_id;
  const isRequester = me === sw.from_user_id;
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-brand-rule/60 last:border-b-0"
         data-testid={`${testidPrefix}-swap-${sw.id}`}>
      <Repeat2 size={13} className="text-brand-violet" />
      <span className="mono-num text-[11.5px] text-brand-muted">
        {sw.from_shift_start.slice(5, 16).replace("T", " ")} ↔ {sw.to_shift_start.slice(5, 16).replace("T", " ")}
      </span>
      <span className="flex-1 text-[12px] text-brand-ink truncate">{sw.note || "—"}</span>
      {isRecipient && (
        <>
          <button className="btn-violet !h-7" onClick={onAccept} disabled={busy}
                  data-testid={`${testidPrefix}-swap-accept-${sw.id}`}>Accept</button>
          <button className="btn-outline !h-7 !text-brand-coral !border-brand-coral/40" onClick={onDecline} disabled={busy}
                  data-testid={`${testidPrefix}-swap-decline-${sw.id}`}>Decline</button>
        </>
      )}
      {isRequester && (
        <button className="btn-outline !h-7 !text-brand-muted" onClick={onCancel} disabled={busy}
                data-testid={`${testidPrefix}-swap-cancel-${sw.id}`}>Cancel</button>
      )}
    </div>
  );
}
